/**
 * useImportWearableCapture — Import WearableCapture entities into Document Studio.
 *
 * Calls the importWearableCaptureToStudio mutation on the core subgraph.
 * The mutation:
 *   1. Fetches the WearableCapture (photo/voice/barcode) from wearables subgraph
 *   2. Registers the existing S3 URI as a ChaosDocument (no re-upload for photos)
 *   3. For voice notes: uses existing transcription or triggers Bedrock Whisper
 *   4. Calls markCaptureImported on the wearables subgraph
 *   5. Returns the new documentId for the chaos pipeline
 *
 * Per-capture progress is tracked so the importer can show individual status.
 * Repository pattern: all data access via Apollo mutations.
 * DRR-105: tenantId + actorId from useAuth/useCurrentTenant (never hardcoded).
 */

import { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';
import type { UploadResult } from './useChaosDocumentUpload';

// ============================================================
// GraphQL
// ============================================================

const IMPORT_WEARABLE_CAPTURE = gql`
  mutation ImportWearableCaptureToStudio($input: ImportWearableCaptureInput!) {
    importWearableCaptureToStudio(input: $input) {
      documentId
      documentStatus
      transcriptionPending
    }
  }
`;

// ============================================================
// Types
// ============================================================

export type ImportCaptureStatus = 'pending' | 'importing' | 'done' | 'error';

export interface ImportResult {
  captureId: string;
  documentId: string | null;
  error: string | null;
}

export interface UseImportWearableCaptureReturn {
  importCaptures: (captureIds: string[]) => Promise<ImportResult[]>;
  isImporting: boolean;
  importProgress: Map<string, ImportCaptureStatus>;
  results: ImportResult[];
  reset: () => void;
}

// ============================================================
// Hook
// ============================================================

export function useImportWearableCapture(): UseImportWearableCaptureReturn {
  const { tenantId } = useCurrentTenant();
  const [importMutation] = useMutation<Record<string, any>>(IMPORT_WEARABLE_CAPTURE);

  const [importProgress, setImportProgress] = useState<Map<string, ImportCaptureStatus>>(new Map());
  const [results, setResults] = useState<ImportResult[]>([]);
  const [isImporting, setIsImporting] = useState(false);

  const reset = useCallback(() => {
    setImportProgress(new Map());
    setResults([]);
    setIsImporting(false);
  }, []);

  const importCaptures = useCallback(
    async (captureIds: string[]): Promise<ImportResult[]> => {
      if (!tenantId) throw new Error('Tenant ID required for import');

      setIsImporting(true);

      // Initialize all to pending
      setImportProgress(new Map(captureIds.map((id) => [id, 'pending'])));
      setResults([]);

      const importResults: ImportResult[] = [];

      // Process sequentially to avoid overwhelming the backend
      for (const captureId of captureIds) {
        setImportProgress((prev) => new Map(prev).set(captureId, 'importing'));

        try {
          const { data } = await importMutation({
            variables: { input: { captureId, tenantId } },
          });

          const docId: string = data?.importWearableCaptureToStudio?.documentId ?? null;
          setImportProgress((prev) => new Map(prev).set(captureId, 'done'));
          importResults.push({ captureId, documentId: docId, error: null });
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : 'Import failed';
          setImportProgress((prev) => new Map(prev).set(captureId, 'error'));
          importResults.push({ captureId, documentId: null, error: errMsg });
        }
      }

      setResults(importResults);
      setIsImporting(false);
      return importResults;
    },
    [tenantId, importMutation]
  );

  return {
    importCaptures,
    isImporting,
    importProgress,
    results,
    reset,
  };
}
